/**
 * Export Colour Schedule dialog — painter side.
 *
 * Downloads a PDF built by the reusable Document Engine
 * (`/api/pro/cms/schemes/{id}/exports/colour-schedule`). Trade
 * equivalents can be toggled at export time; SSOT is enforced server-side.
 */

import { useState } from "react";
import { Loader2, FileDown, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ExportScheduleDialog({ open, onClose, scheme }) {
  const [includeTrade, setIncludeTrade] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open || !scheme) return null;

  async function handleDownload() {
    setBusy(true);
    try {
      const res = await api.post(
        `/pro/cms/schemes/${scheme.id}/exports/colour-schedule`,
        { include_trade: includeTrade },
        { responseType: "blob" },
      );
      const disp = res.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `colour-schedule-${scheme.id}.pdf`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Colour schedule downloaded.");
      onClose?.();
    } catch (err) {
      // Blob error responses need a manual read
      let detail = "Export failed.";
      try {
        if (err.response?.data && err.response.data instanceof Blob) {
          const txt = await err.response.data.text();
          try {
            detail = JSON.parse(txt).detail || detail;
          } catch {
            detail = txt || detail;
          }
        } else {
          detail = err.response?.data?.detail || err.message || detail;
        }
      } catch {
        /* keep default */
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-brand-ink/50 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="export-schedule-dialog"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-brand-muted hover:text-brand-ink"
          data-testid="export-schedule-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="overline mb-2 text-brand-teal">Export</div>
        <h3 className="font-serif text-xl text-brand-ink mb-1">Colour schedule PDF</h3>
        <p className="text-xs text-brand-muted mb-5">
          A professional, print-ready PDF built from the current revision of{" "}
          <span className="text-brand-ink">{scheme.name}</span>. Uses your business profile for
          branding.
        </p>

        <label
          className="flex items-start gap-3 border border-brand-linen p-3 cursor-pointer hover:border-brand-teal transition-colors"
          data-testid="export-schedule-trade-toggle-label"
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeTrade}
            onChange={(e) => setIncludeTrade(e.target.checked)}
            data-testid="export-schedule-trade-toggle"
          />
          <div>
            <div className="text-sm text-brand-ink font-medium">Include trade equivalents</div>
            <div className="text-xs text-brand-muted mt-0.5">
              Appends a purchasing reference table (Dulux / Taubmans / Resene …). Your approved
              ColourMatch library colour always remains the official project colour.
            </div>
          </div>
        </label>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="text-xs h-9 px-3 border border-brand-linen text-brand-muted hover:text-brand-ink"
            data-testid="export-schedule-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={busy}
            data-testid="export-schedule-download"
            className="inline-flex items-center gap-2 text-xs h-9 px-4 bg-brand-navy text-white hover:bg-brand-teal/90 disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}
